/** Parse / serialize JSON site_settings for visual admin editors (mirrors public site defaults). */

import { normalizeFooterCategoryItem } from './footerCategoryItem.js';

export const DEFAULT_QUICK_LINKS = [
  { label: 'About Us', path: '/about' },
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Careers', path: '/careers' },
  { label: 'Blog', path: '/blogs' },
  { label: 'Contact', path: '/contact' }
];

export const DEFAULT_FOOTER_CATEGORIES = [
  {
    heading: 'Trending',
    items: [
      { label: 'Kedarkantha Trek', search: 'Kedarkantha Trek' },
      { label: 'Valley of Flowers', search: 'Valley of Flowers' },
      { label: 'Hampta Pass', search: 'Hampta Pass' },
      { label: 'Sar Pass Trek', search: 'Sar Pass Trek' }
    ]
  },
  {
    heading: 'Region',
    items: [
      { label: 'Himalayas', search: 'Himalayas' },
      { label: 'Himachal Pradesh', search: 'Himachal Pradesh' },
      { label: 'Uttarakhand', search: 'Uttarakhand' },
      { label: 'Kashmir', search: 'Kashmir' }
    ]
  },
  {
    heading: 'Experience',
    items: [
      { label: 'High Altitude Trek', search: 'High Altitude Trek' },
      { label: 'Snow Trek', search: 'Snow Trek' },
      { label: 'Backpacking', search: 'Backpacking' },
      { label: 'Camping', search: 'Camping' }
    ]
  },
  {
    heading: 'Duration',
    items: [
      { label: 'Weekend Getaways', search: 'Weekend' },
      { label: 'Long Expeditions', search: 'Expedition' },
      { label: 'Day Hikes', search: 'Day Hike' },
      { label: 'Summer Camps', search: 'Summer Camp' }
    ]
  }
];

export const DEFAULT_INITIATIVES_CARDS = [
  {
    title: 'Environmental Conservation',
    description:
      "We're committed to preserving the natural beauty of the places we explore through responsible tourism and conservation efforts."
  },
  {
    title: 'Community Support',
    description:
      'Supporting local communities in adventure destinations through partnerships and sustainable tourism practices.'
  },
  {
    title: 'Education & Awareness',
    description:
      'Promoting outdoor education and environmental awareness through workshops, seminars, and educational programs.'
  }
];

export function parseQuickLinks(raw) {
  if (!String(raw || '').trim()) return DEFAULT_QUICK_LINKS.map((x) => ({ ...x }));
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return DEFAULT_QUICK_LINKS.map((x) => ({ ...x }));
    return parsed.map((item) => ({
      label: String(item?.label || '').trim(),
      path: String(item?.path || '').trim()
    }));
  } catch {
    return DEFAULT_QUICK_LINKS.map((x) => ({ ...x }));
  }
}

export function serializeQuickLinks(links) {
  const clean = (links || [])
    .map((item) => ({
      label: String(item?.label || '').trim(),
      path: String(item?.path || '').trim()
    }))
    .filter((item) => item.label && item.path);
  return JSON.stringify(clean);
}

export function parseFooterCategories(raw) {
  if (!String(raw || '').trim()) return DEFAULT_FOOTER_CATEGORIES.map((g) => ({
    heading: g.heading,
    items: g.items.map((i) => ({ ...i }))
  }));
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) {
      return DEFAULT_FOOTER_CATEGORIES.map((g) => ({
        heading: g.heading,
        items: g.items.map((i) => ({ ...i }))
      }));
    }
    return parsed.map((g) => ({
      heading: String(g?.heading || '').trim(),
      items: (Array.isArray(g?.items) ? g.items : []).map((item) => normalizeFooterCategoryItem(item))
    }));
  } catch {
    return DEFAULT_FOOTER_CATEGORIES.map((g) => ({
      heading: g.heading,
      items: g.items.map((i) => ({ ...i }))
    }));
  }
}

/** Editor: keep empty rows so “Add link” works while typing. */
export function serializeFooterCategories(groups, { forEditor = false } = {}) {
  const mapped = (groups || []).map((g) => ({
    heading: String(g?.heading || '').trim(),
    items: (g?.items || []).map((item) => normalizeFooterCategoryItem(item))
  }));

  if (forEditor) {
    return JSON.stringify(mapped);
  }

  const clean = mapped
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (item) =>
          item.label &&
          (String(item.search || '').trim() || (item.trekSlugs && item.trekSlugs.length))
      )
    }))
    .filter((g) => g.heading && g.items.length);
  return JSON.stringify(clean);
}

/** Strip empty draft rows before saving site settings. */
export function cleanFooterCategoriesForSave(rawJson) {
  const groups = parseFooterCategories(rawJson);
  return serializeFooterCategories(groups, { forEditor: false });
}

export function parseInitiativesCards(raw) {
  if (!String(raw || '').trim()) return DEFAULT_INITIATIVES_CARDS.map((c) => ({ ...c }));
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return DEFAULT_INITIATIVES_CARDS.map((c) => ({ ...c }));
    return parsed.map((item) => ({
      title: String(item?.title || '').trim(),
      description: String(item?.description || '').trim()
    }));
  } catch {
    return DEFAULT_INITIATIVES_CARDS.map((c) => ({ ...c }));
  }
}

export function serializeInitiativesCards(cards) {
  const clean = (cards || [])
    .map((item) => ({
      title: String(item?.title || '').trim(),
      description: String(item?.description || '').trim()
    }))
    .filter((item) => item.title);
  return JSON.stringify(clean);
}
